import React, { useEffect, useState, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { useToast } from "./ui/Toast";
import { Button, Card, Select } from "./ui";
import { ConfirmModal } from "./ui/Modal";

const TIPI_DOCUMENTO = [
  { value: 100000005, label: "Certificazione" },
  { value: 100000006, label: "Polizza" },
  { value: 100000007, label: "DURC" },
  { value: 100000008, label: "Visura" },
  { value: 100000011, label: "Altro" },
];

const tipoLabel = (v) =>
  TIPI_DOCUMENTO.find((t) => t.value === v)?.label || `Tipo ${v}`;

/**
 * Tab "Config Doc" nel dettaglio fornitore.
 * Mostra la configurazione effettiva applicata al fornitore (override o macrocategoria)
 * e permette ad admin di creare/modificare override.
 */
function FornitoreConfigDocTab({ fornitoreId }) {
  const { apiRequest, user } = useAuth();
  const { showToast } = useToast();

  const [effettiva, setEffettiva] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const [newTipo, setNewTipo] = useState("");
  const [newObbligatorio, setNewObbligatorio] = useState(true);
  const [newPeso, setNewPeso] = useState("1.0");

  const isAdmin = user?.ruolo === 100000000;

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiRequest(
        `/config-documenti-valutazione/effettiva/${fornitoreId}`,
      );
      setEffettiva(data);
    } catch (error) {
      showToast(error.message || "Errore caricamento config", "error");
    } finally {
      setLoading(false);
    }
  }, [apiRequest, fornitoreId, showToast]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const isOverride = effettiva?.source === "override";

  const tipiNonConfigurati = TIPI_DOCUMENTO.filter(
    (t) =>
      !(effettiva?.config || []).some(
        (r) => r.phc_tipodocumento === t.value && isOverride,
      ),
  );

  const handleAddOverride = async () => {
    if (!newTipo) {
      showToast("Seleziona un tipo documento", "error");
      return;
    }
    setSaving(true);
    try {
      await apiRequest("/config-documenti-valutazione", {
        method: "POST",
        body: JSON.stringify({
          fornitoreId,
          tipodocumento: parseInt(newTipo),
          obbligatorio: newObbligatorio,
          peso: parseFloat(newPeso) || 1.0,
          attivo: true,
        }),
      });
      showToast("Override creato", "success");
      setNewTipo("");
      setNewObbligatorio(true);
      setNewPeso("1.0");
      loadData();
    } catch (error) {
      showToast(error.message || "Errore", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (row, patch) => {
    if (!isOverride) return;
    try {
      await apiRequest(
        `/config-documenti-valutazione/${row.phc_configdocumentivalutazioneid}`,
        { method: "PUT", body: JSON.stringify(patch) },
      );
      loadData();
    } catch (error) {
      showToast(error.message || "Errore", "error");
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    try {
      await apiRequest(
        `/config-documenti-valutazione/${confirmDelete.phc_configdocumentivalutazioneid}`,
        { method: "DELETE" },
      );
      showToast("Override eliminato", "success");
      setConfirmDelete(null);
      loadData();
    } catch (error) {
      showToast(error.message || "Errore", "error");
    }
  };

  const handleConvertToOverride = async () => {
    // Crea override copiando tutti i tipi della macrocategoria
    if (!effettiva?.config?.length) {
      showToast("Nessuna config da copiare", "error");
      return;
    }
    setSaving(true);
    try {
      for (const r of effettiva.config) {
        await apiRequest("/config-documenti-valutazione", {
          method: "POST",
          body: JSON.stringify({
            fornitoreId,
            tipodocumento: r.phc_tipodocumento,
            obbligatorio: !!r.phc_obbligatorio,
            peso: r.phc_peso || 1.0,
            attivo: true,
          }),
        });
      }
      showToast("Override creato. Personalizza le righe sotto.", "success");
      loadData();
    } catch (error) {
      showToast(error.message || "Errore creazione override", "error");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="text-sm text-gray-400 py-6 text-center">
        Caricamento...
      </div>
    );
  }

  const sourceBadge = {
    override: {
      bg: "bg-amber-100",
      text: "text-amber-800",
      label: "Override fornitore",
    },
    macrocategoria: {
      bg: "bg-blue-100",
      text: "text-blue-800",
      label: "Eredita da macrocategoria",
    },
    default: {
      bg: "bg-gray-100",
      text: "text-gray-700",
      label: "Default di sistema",
    },
  }[effettiva?.source || "default"];

  return (
    <div className="space-y-4">
      <Card className="p-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h3 className="font-semibold text-gray-900">
              Configurazione documenti valutazione
            </h3>
            <p className="text-xs text-gray-500 mt-1">
              Tipi documento e pesi usati dal KPI "Completezza documentale".
            </p>
          </div>
          <span
            className={`text-xs px-2 py-1 rounded ${sourceBadge.bg} ${sourceBadge.text}`}
          >
            {sourceBadge.label}
          </span>
        </div>

        {!isOverride && isAdmin && effettiva?.source === "macrocategoria" && (
          <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded text-sm">
            <p className="text-amber-800 mb-2">
              Questo fornitore eredita la configurazione dalla macrocategoria.
              Per personalizzare i requisiti documentali, crea un override.
            </p>
            <Button
              size="sm"
              onClick={handleConvertToOverride}
              disabled={saving}
            >
              {saving ? "..." : "Crea override fornitore"}
            </Button>
          </div>
        )}
      </Card>

      <Card>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left px-4 py-3 font-medium text-gray-600">
                  Tipo Documento
                </th>
                <th className="text-center px-4 py-3 font-medium text-gray-600 w-32">
                  Obbligatorio
                </th>
                <th className="text-center px-4 py-3 font-medium text-gray-600 w-32">
                  Peso
                </th>
                {isOverride && isAdmin && (
                  <th className="text-right px-4 py-3 font-medium text-gray-600 w-24">
                    Azioni
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {(effettiva?.config || []).length === 0 ? (
                <tr>
                  <td
                    colSpan={isOverride && isAdmin ? 4 : 3}
                    className="text-center py-6 text-gray-400"
                  >
                    Nessuna configurazione: si applicano i default di sistema
                    (Cert/Polizza/DURC obbligatori, Visura bonus)
                  </td>
                </tr>
              ) : (
                effettiva.config.map((r) => (
                  <tr
                    key={r.phc_configdocumentivalutazioneid}
                    className="border-b hover:bg-gray-50"
                  >
                    <td className="px-4 py-3 font-medium">
                      {tipoLabel(r.phc_tipodocumento)}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <input
                        type="checkbox"
                        disabled={!isOverride || !isAdmin}
                        checked={!!r.phc_obbligatorio}
                        onChange={(e) =>
                          handleUpdate(r, { obbligatorio: e.target.checked })
                        }
                        className="h-4 w-4"
                      />
                    </td>
                    <td className="px-4 py-3 text-center">
                      <input
                        type="number"
                        min="0"
                        max="10"
                        step="0.1"
                        disabled={!isOverride || !isAdmin}
                        defaultValue={r.phc_peso}
                        onBlur={(e) => {
                          const v = parseFloat(e.target.value);
                          if (!isNaN(v) && v !== r.phc_peso) {
                            handleUpdate(r, { peso: v });
                          }
                        }}
                        className="w-20 text-center rounded border-gray-300 text-xs disabled:bg-gray-50"
                      />
                    </td>
                    {isOverride && isAdmin && (
                      <td className="px-4 py-3 text-right">
                        <Button
                          size="sm"
                          variant="danger"
                          onClick={() => setConfirmDelete(r)}
                        >
                          Elimina
                        </Button>
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {isOverride && isAdmin && tipiNonConfigurati.length > 0 && (
        <Card className="p-4">
          <h4 className="font-semibold text-gray-900 mb-3">
            Aggiungi tipo documento all'override
          </h4>
          <div className="flex flex-wrap items-end gap-3">
            <div className="min-w-[200px]">
              <label className="block text-xs text-gray-500 mb-1">
                Tipo documento
              </label>
              <Select
                value={newTipo}
                onChange={(e) => setNewTipo(e.target.value)}
              >
                <option value="">Seleziona...</option>
                {tipiNonConfigurati.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </Select>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={newObbligatorio}
                onChange={(e) => setNewObbligatorio(e.target.checked)}
                className="h-4 w-4"
              />
              Obbligatorio
            </label>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Peso</label>
              <input
                type="number"
                min="0"
                max="10"
                step="0.1"
                value={newPeso}
                onChange={(e) => setNewPeso(e.target.value)}
                className="w-24 rounded border-gray-300 text-sm"
              />
            </div>
            <Button onClick={handleAddOverride} disabled={saving || !newTipo}>
              {saving ? "..." : "Aggiungi"}
            </Button>
          </div>
        </Card>
      )}

      <ConfirmModal
        isOpen={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        onConfirm={handleDelete}
        title="Elimina override"
        message={
          confirmDelete
            ? `Eliminare override per ${tipoLabel(confirmDelete.phc_tipodocumento)}?`
            : ""
        }
        confirmText="Elimina"
        variant="danger"
      />
    </div>
  );
}

export default FornitoreConfigDocTab;
